import React from 'react';
import { Heart, Facebook, Instagram, Youtube, Linkedin, ArrowUp } from 'lucide-react';
import { contactData, navLinks, logoUrl } from '../data/mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialLinks = [
    { icon: Facebook, href: contactData.socialMedia.facebook, label: 'Facebook' },
    { icon: Instagram, href: contactData.socialMedia.instagram, label: 'Instagram' },
    { icon: Youtube, href: contactData.socialMedia.youtube, label: 'YouTube' },
    { icon: Linkedin, href: contactData.socialMedia.linkedin, label: 'LinkedIn' }
  ];

  return (
    <footer className="bg-slate-900 text-white relative">
      {/* Scroll to Top Button */}
      <div className="absolute -top-6 left-1/2 -translate-x-1/2">
        <button
          onClick={scrollToTop}
          className="w-12 h-12 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={logoUrl} 
                alt="DC Early Learning Collaborative" 
                className="w-14 h-14 rounded-full object-cover"
              />
              <div>
                <p className="font-bold text-lg text-white">DC Early Learning Collaborative</p>
                <p className="text-sm text-slate-400">Building a Thriving Future</p>
              </div>
            </div>
            <p className="text-slate-400 leading-relaxed mb-6 max-w-md">
              A city where every child from birth to age 5 has equitable access to high-quality early education and care.
            </p>
            {/* Social Links */}
            <div className="flex gap-4 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-slate-400 hover:bg-teal-500 hover:text-white transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            
            {/* Donate Button */}
            <a
              href="https://www.zeffy.com/en-US/donation-form/help-build-an-equitable-accessible-and-affordable-early-childhood-system-in-the-district-of-columbia"
              target="_blank"
              rel="noopener noreferrer"
              data-donate-link="true"
              aria-label="Donate to DC Early Learning Collaborative (opens in a new tab)"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#079897] hover:bg-[#067a79] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <Heart className="w-5 h-5 mr-2" />
              Donate
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-teal-400 transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${contactData.phone}`}
                  className="text-slate-400 hover:text-teal-400 transition-colors duration-300"
                >
                  {contactData.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${contactData.email}`}
                  className="text-slate-400 hover:text-teal-400 transition-colors duration-300"
                >
                  {contactData.email}
                </a>
              </li>
              <li>
                <span className="text-slate-400">{contactData.address}</span>
              </li>
              <li>
                <span className="text-slate-400">{contactData.city}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} DC Early Learning Collaborative. All rights reserved.
              {' '}&bull;{' '}
              <a
                href="/accessibility"
                className="text-slate-400 underline hover:text-teal-400 transition-colors duration-300"
                data-testid="footer-accessibility-link"
              >
                Accessibility
              </a>
            </p>
            <p className="text-slate-500 text-sm flex items-center gap-1">
              Made with <Heart className="w-4 h-4 text-rose-500" /> for DC Children
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
